/**
 * The idempotency key every write carries.
 *
 * `crypto.randomUUID()` is a browser API. React Native does not define a
 * `crypto` global at all, so calling it directly — which this package used to
 * do — throws `undefined is not an object` on the phone the moment anyone logs
 * food. The web never noticed because the web has it.
 *
 * This is not a security token. It is a dedupe key, scoped per user by
 * `(identity_id, client_id)` on the server, and its only job is to be different
 * from the last one so a replayed offline queue doesn't log dinner twice. So
 * the fallback path is fine: `Math.random` is a poor source of secrets and a
 * perfectly good source of distinct values.
 *
 * Ordered strongest first, and the strong paths are used wherever they exist.
 */

interface MaybeCrypto {
  randomUUID?: () => string;
  getRandomValues?: <T extends ArrayBufferView>(array: T) => T;
}

/** RFC 4122 v4, laid out from 16 bytes however we got them. */
function format(bytes: Uint8Array): string {
  bytes[6] = ((bytes[6] as number) & 0x0f) | 0x40; // version 4
  bytes[8] = ((bytes[8] as number) & 0x3f) | 0x80; // variant 10
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function clientId(): string {
  const source = (globalThis as { crypto?: MaybeCrypto }).crypto;

  if (typeof source?.randomUUID === "function") return source.randomUUID();

  const bytes = new Uint8Array(16);
  if (typeof source?.getRandomValues === "function") {
    source.getRandomValues(bytes);
    return format(bytes);
  }

  for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  return format(bytes);
}
